import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslations } from "@/hooks/use-translations";
import { api, ApiError, isStaleAnswer } from "@/lib/api";

/**
 * Delete / Cancel on a deletion the ads agent asked to confirm (M-05). The
 * server decides who may delete; the prompt stays on screen until it accepts.
 */
export function useDeletionAnswer({
	projectId,
	clearQuestion,
	resyncQuestion,
}: {
	projectId: string | undefined;
	clearQuestion: () => string | undefined;
	resyncQuestion: () => void;
}) {
	const t = useTranslations("workspace");
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: ({ questionId, choice }: { questionId: string; choice: "delete" | "cancel" }) =>
			api.answerDeletion(projectId!, questionId, choice),
		onSuccess: (result) => {
			clearQuestion();
			toast.info(t(result.status === "confirmed" ? "deletion.confirmed" : "deletion.cancelled"));
			queryClient.invalidateQueries({ queryKey: ["chatHistory", projectId] });
		},
		onError: (err) => {
			if (err instanceof ApiError && err.code === "deletion_forbidden") {
				toast.info(t("deletion.forbidden"));
				return;
			}
			if (isStaleAnswer(err)) {
				toast.info(t("questionChanged"));
				resyncQuestion();
				return;
			}
			toast.error(err instanceof Error ? err.message : t("deletion.failed"));
		},
	});
	return {
		answer: (questionId: string | undefined, choice: "delete" | "cancel") => {
			if (!projectId || !questionId || mutation.isPending) return;
			mutation.mutate({ questionId, choice });
		},
		isAnswering: mutation.isPending,
	};
}
